import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { SubmitButton } from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import { Icon, useCustomTheme } from "@luna-restaurant/ui";

import { SecondaryButton } from "@/components/SecondaryButton";
import {
  ClosingTimeForm,
  type ClosingTimeFormValues,
} from "@/components/forms/ClosingTimeForm";
import { ChefHatModal } from "@/components/modals/ChefHatModal";

type EditClosingTimeModalProps = {
  visible: boolean;
  closingTime?: Partial<ClosingTimeFormValues> | null;
  onDismiss: () => void;
  onSubmit: (values: ClosingTimeFormValues) => Promise<void>;
  schoolClassOptions: { label: string; value: string }[];
};

const hasEffectiveEditChanges = (
  values: ClosingTimeFormValues,
  initialValues?: Partial<ClosingTimeFormValues>,
) => {
  if (!initialValues) {
    return true;
  }

  const normalizeText = (value?: string | null) => (value ?? "").trim();

  return (
    normalizeText(values.editor) !== normalizeText(initialValues.editor) ||
    normalizeText(values.closingNotice) !==
      normalizeText(initialValues.closingNotice) ||
    values.dateFrom !== (initialValues.dateFrom ?? "") ||
    values.dateTo !== (initialValues.dateTo ?? "")
  );
};

export function EditClosingTimeModal({
  visible,
  closingTime,
  onDismiss,
  onSubmit,
  schoolClassOptions,
}: EditClosingTimeModalProps) {
  const { colors, fonts } = useCustomTheme();
  const { t } = useTranslation();

  const initialValues = useMemo(() => {
    if (!closingTime) return undefined;
    return {
      editor: closingTime.editor,
      closingNotice: closingTime.closingNotice,
      dateFrom: closingTime.dateFrom,
      dateTo: closingTime.dateTo,
      scopeOrClassId: closingTime.scopeOrClassId,
    } satisfies Partial<ClosingTimeFormValues>;
  }, [closingTime]);

  return (
    <ChefHatModal
      visible={visible}
      icon={{ icon: "pencil", iconSet: "material-community" }}
      iconSize={50}
      onDismiss={onDismiss}
    >
      <View style={styles.titleContainer}>
        <Icon
          iconSet="material-community"
          icon="pencil"
          color={colors.success}
        />
        <Text style={[styles.titleText, fonts.bold, { color: colors.success }]}>
          {t("closeTimes.editModal.title")}
        </Text>
      </View>

      <ClosingTimeForm
        schoolClassOptions={schoolClassOptions}
        initialValues={initialValues}
        isSubmitDisabled={(values) =>
          !hasEffectiveEditChanges(values, initialValues)
        }
        actionsNotice={
          <Text
            style={[
              styles.reviewNoticeText,
              fonts.regular,
              { color: colors.text },
            ]}
          >
            {t("closeTimes.editModal.reviewNotice")}
          </Text>
        }
        onSubmit={onSubmit}
        renderActions={({ submit, reset, isSubmitting, isSubmitDisabled }) => (
          <View style={styles.actionsContainer}>
            <SecondaryButton
              label={t("closeTimes.editModal.cancelButtonText")}
              onPress={() => {
                if (isSubmitting) return;
                reset();
                onDismiss();
              }}
              disabled={isSubmitting}
              iconName="close"
              iconPosition="left"
            />

            <SubmitButton
              title={t("closeTimes.editModal.submitButtonText")}
              onPress={submit}
              disabled={isSubmitting || isSubmitDisabled}
              buttonStyle={[
                styles.primaryButton,
                { backgroundColor: colors.border },
              ]}
              buttonTextStyle={[
                styles.primaryButtonText,
                fonts.bold,
                { color: colors.background },
              ]}
              iconName="arrow-right"
              iconColor={colors.background}
              iconPosition="right"
              iconSize={24}
            />
          </View>
        )}
      />
    </ChefHatModal>
  );
}

const styles = StyleSheet.create({
  titleContainer: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 40,
  },
  titleText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  actionsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    marginTop: 10,
  },
  reviewNoticeText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  primaryButton: {
    borderRadius: 56,
    height: 56,
    paddingHorizontal: 24,
  },
  primaryButtonText: {
    fontSize: 16,
  },
});
